/**
 * Filtros avançados de uma lista — derivados do descriptor, iguais para toda entidade.
 *
 * A busca livre resolve "achar o fulano". Não resolve "pessoas entre 60 e 75 anos, nascidas depois
 * de 1960, sem CPF" — e é isso que uma base de dezenas de milhares de registros exige. Como cada
 * campo do descriptor já declara o seu `tipo`, o widget e os operadores saem daí: nenhuma entidade
 * precisa escrever a sua própria tela de filtro, e uma entidade nova nasce filtrável.
 *
 * O desenho é "linha por filtro", não um formulário com todos os campos. Pessoa tem 30 campos
 * filtráveis; renderizar 30 controles de uma vez seria uma parede onde ninguém acha nada. Aqui o
 * operador acrescenta as condições que quer, e cada linha escolhe campo → operador → valor.
 */
import { Filter, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { DefCampo, DescritorRecurso, RecursoBase, TipoCampo } from '@/lib/recursos/tipos';

/** Uma condição: `nome contém "silva"`, `idade de 60`. É o que vai no query param `filtros`. */
export type Filtro = { campo: string; op: OperadorFiltro; valor: string | string[] };

export type OperadorFiltro = 'contem' | 'igual' | 'de' | 'ate' | 'em' | 'vazio' | 'preenchido';

const ROTULO_OP: Record<OperadorFiltro, string> = {
  contem: 'contém',
  igual: 'é igual a',
  de: 'a partir de',
  ate: 'até',
  em: 'é um de',
  vazio: 'está vazio',
  preenchido: 'está preenchido',
};

/**
 * Operadores que fazem sentido para cada tipo de campo. `vazio`/`preenchido` valem para todos —
 * "quem ainda não tem CPF" é pergunta de higiene de base, e some se não estiver aqui.
 */
const OPS_POR_TIPO: Record<string, OperadorFiltro[]> = {
  texto: ['contem', 'igual'],
  'texto-longo': ['contem'],
  documento: ['contem', 'igual'],
  placa: ['contem', 'igual'],
  telefone: ['contem'],
  numero: ['de', 'ate', 'igual'],
  dinheiro: ['de', 'ate', 'igual'],
  data: ['de', 'ate', 'igual'],
  select: ['igual', 'em'],
  referencia: ['igual'],
  booleano: ['igual'],
  leitura: ['contem'],
};
const OPS_UNIVERSAIS: OperadorFiltro[] = ['vazio', 'preenchido'];

function opsDe(tipo: TipoCampo): OperadorFiltro[] {
  return [...(OPS_POR_TIPO[tipo] ?? ['contem']), ...OPS_UNIVERSAIS];
}

/** Todos os campos filtráveis: os do formulário de edição + os declarados só para filtro. */
export function camposFiltraveis<T extends RecursoBase>(d: DescritorRecurso<T>): DefCampo<T>[] {
  const vistos = new Set<string>();
  const saida: DefCampo<T>[] = [];
  for (const c of [...(d.gruposEdicao ?? []).flatMap((g) => g.campos), ...(d.camposFiltro ?? [])]) {
    if (vistos.has(c.chave)) continue;
    vistos.add(c.chave);
    saida.push(c);
  }
  return saida.sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
}

/** Select assíncrono de uma FK (profissão, segmento…) — as mesmas opções da tela de edição. */
function SelectReferencia({
  campo,
  valor,
  aoMudar,
}: {
  campo: DefCampo<RecursoBase>;
  valor: string;
  aoMudar: (v: string) => void;
}) {
  const [opcoes, setOpcoes] = useState<{ id: string; rotulo: string }[]>([]);
  const rotuloCampo = campo.refRotulo ?? 'nome';
  useEffect(() => {
    if (!campo.refEndpoint) return;
    void api.get<Record<string, unknown>[]>(campo.refEndpoint).then((r) => {
      if (!r.ok) return;
      setOpcoes(
        r.dados.map((o) => ({ id: String(o.id), rotulo: String(o[rotuloCampo] ?? o.id) })),
      );
    });
  }, [campo.refEndpoint, rotuloCampo]);
  return (
    <select className={CLASSE_CONTROLE} value={valor} onChange={(e) => aoMudar(e.target.value)}>
      <option value="">—</option>
      {opcoes.map((o) => (
        <option key={o.id} value={o.id}>
          {o.rotulo}
        </option>
      ))}
    </select>
  );
}

const CLASSE_CONTROLE =
  'h-9 min-w-0 rounded-md border border-border bg-surface px-2 text-[14px] text-text outline-none focus-visible:shadow-[var(--focus-ring)]';

/** O controle de valor, escolhido pelo tipo do campo. `vazio`/`preenchido` não pedem valor. */
function ControleValor({
  campo,
  op,
  valor,
  valorLista,
  aoMudar,
  aoMudarLista,
}: {
  campo: DefCampo<RecursoBase>;
  op: OperadorFiltro;
  valor: string;
  valorLista: string[];
  aoMudar: (v: string) => void;
  aoMudarLista: (v: string[]) => void;
}) {
  if (op === 'vazio' || op === 'preenchido') return <span className="text-text-tertiary">—</span>;
  // "é um de": vocabulário com várias opções aceitas ao mesmo tempo (viúvo OU divorciado). O valor
  // vira lista, que é o que o operador `em` do servidor espera.
  if (op === 'em' && campo.tipo === 'select') {
    return (
      <select
        multiple
        size={Math.min(4, (campo.opcoes ?? []).length || 1)}
        className={`${CLASSE_CONTROLE} h-auto py-1`}
        value={Array.isArray(valorLista) ? valorLista : []}
        onChange={(e) =>
          aoMudarLista(Array.from(e.target.selectedOptions).map((o) => o.value))
        }
      >
        {(campo.opcoes ?? []).map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    );
  }
  if (campo.tipo === 'select') {
    return (
      <select className={CLASSE_CONTROLE} value={valor} onChange={(e) => aoMudar(e.target.value)}>
        <option value="">—</option>
        {(campo.opcoes ?? []).map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    );
  }
  if (campo.tipo === 'booleano') {
    return (
      <select className={CLASSE_CONTROLE} value={valor} onChange={(e) => aoMudar(e.target.value)}>
        <option value="">—</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </select>
    );
  }
  if (campo.tipo === 'referencia') {
    return <SelectReferencia campo={campo} valor={valor} aoMudar={aoMudar} />;
  }
  const tipoInput =
    campo.tipo === 'data' ? 'date' : campo.tipo === 'numero' || campo.tipo === 'dinheiro' ? 'number' : 'text';
  return (
    <input
      type={tipoInput}
      className={CLASSE_CONTROLE}
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      placeholder={campo.tipo === 'data' ? '' : 'valor'}
    />
  );
}

export function FiltrosAvancados<T extends RecursoBase>({
  descritor,
  filtros,
  aoAplicar,
}: {
  descritor: DescritorRecurso<T>;
  filtros: Filtro[];
  aoAplicar: (f: Filtro[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const campos = useMemo(
    () => camposFiltraveis(descritor) as DefCampo<RecursoBase>[],
    [descritor],
  );
  const porChave = useMemo(() => new Map(campos.map((c) => [c.chave, c])), [campos]);

  // Rascunho: o operador monta as condições e aplica de uma vez. Aplicar a cada tecla dispararia
  // uma consulta por caractere numa tabela de 70 mil linhas.
  const [rascunho, setRascunho] = useState<Filtro[]>(filtros);
  useEffect(() => setRascunho(filtros), [filtros]);

  function acrescentar() {
    const primeiro = campos[0];
    if (!primeiro) return;
    setRascunho((r) => [...r, { campo: primeiro.chave, op: opsDe(primeiro.tipo)[0], valor: '' }]);
  }
  function alterar(i: number, mudanca: Partial<Filtro>) {
    setRascunho((r) => r.map((f, j) => (j === i ? { ...f, ...mudanca } : f)));
  }
  function remover(i: number) {
    setRascunho((r) => r.filter((_, j) => j !== i));
  }

  const ativos = filtros.length;

  // Fragmento, não wrapper: o botão e o painel viram irmãos diretos da linha da busca. Assim o
  // botão fica AO LADO do campo de busca, e só o painel aberto (`basis-full`) toma a linha inteira
  // — fechado, os filtros não gastam uma linha à toa.
  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant="secundario"
          onClick={() => setAberto((a) => !a)}
          aria-expanded={aberto}
          title="Filtros avançados"
        >
          <Filter className="size-4" />
          Filtros
          {ativos > 0 && (
            <span className="ml-1 rounded-full bg-accent px-1.5 text-[12px] text-on-primary">
              {ativos}
            </span>
          )}
        </Button>
        {ativos > 0 && !aberto && (
          <button
            type="button"
            onClick={() => aoAplicar([])}
            className="tipo-corpo-pequeno text-text-tertiary underline-offset-2 hover:underline"
          >
            limpar
          </button>
        )}
      </div>

      {aberto && (
        <div className="flex basis-full flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
          {rascunho.length === 0 && (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Nenhuma condição. Acrescente uma para refinar a lista.
            </p>
          )}
          {rascunho.map((f, i) => {
            const campo = porChave.get(f.campo);
            const ops = campo ? opsDe(campo.tipo) : (['contem'] as OperadorFiltro[]);
            return (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select
                  className={`${CLASSE_CONTROLE} min-w-44`}
                  value={f.campo}
                  onChange={(e) => {
                    const novo = porChave.get(e.target.value);
                    // Trocar de campo pode invalidar o operador (data não "contém"): volta ao 1º.
                    alterar(i, {
                      campo: e.target.value,
                      op: novo ? opsDe(novo.tipo)[0] : 'contem',
                      valor: '',
                    });
                  }}
                >
                  {campos.map((c) => (
                    <option key={c.chave} value={c.chave}>
                      {c.rotulo}
                    </option>
                  ))}
                </select>
                <select
                  className={`${CLASSE_CONTROLE} min-w-32`}
                  value={f.op}
                  onChange={(e) => {
                    const op = e.target.value as OperadorFiltro;
                    alterar(i, { op, valor: op === 'em' ? [] : '' });
                  }}
                >
                  {ops.map((o) => (
                    <option key={o} value={o}>
                      {ROTULO_OP[o]}
                    </option>
                  ))}
                </select>
                {campo && (
                  <ControleValor
                    campo={campo}
                    op={f.op}
                    valor={typeof f.valor === 'string' ? f.valor : ''}
                    valorLista={Array.isArray(f.valor) ? f.valor : []}
                    aoMudar={(v) => alterar(i, { valor: v })}
                    aoMudarLista={(v) => alterar(i, { valor: v })}
                  />
                )}
                <button
                  type="button"
                  onClick={() => remover(i)}
                  aria-label="Remover condição"
                  className="rounded-md p-1.5 text-text-tertiary hover:bg-surface-hover hover:text-danger"
                >
                  <X className="size-4" />
                </button>
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-1">
            <Button variant="secundario" onClick={acrescentar}>
              <Plus className="size-4" />
              Condição
            </Button>
            <Button variant="primario" onClick={() => aoAplicar(rascunho)}>
              Aplicar
            </Button>
            {rascunho.length > 0 && (
              <Button
                variant="secundario"
                onClick={() => {
                  setRascunho([]);
                  aoAplicar([]);
                }}
              >
                Limpar
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
